/**
 * The single source of truth for every shape that crosses a trust boundary:
 * the Worker API, the Shortcut endpoint, CSV/JSON imports, backups, and URL
 * capture parameters. The PWA and the Worker both import these schemas so a
 * payload can never be validated one way on the client and another on the edge.
 */
import { z } from 'zod';
import { parseAmountToSen } from './money.js';

export const TRANSACTION_TYPES = ['expense', 'income', 'refund', 'transfer'] as const;
export type TransactionType = (typeof TRANSACTION_TYPES)[number];

export const PAYMENT_SOURCES = [
  'manual',
  'apple-pay-shortcut',
  'maybank-quick-capture',
  'tng-quick-capture',
  'maybank-receipt',
  'tng-receipt',
  'csv-import',
  'json-import',
] as const;
export type PaymentSource = (typeof PAYMENT_SOURCES)[number];

export const REVIEW_STATUSES = ['confirmed', 'needs-review'] as const;
export type ReviewStatus = (typeof REVIEW_STATUSES)[number];

export const SYNC_STATES = ['local', 'pending', 'synced', 'failed'] as const;
export type SyncState = (typeof SYNC_STATES)[number];

/** Sources the user never typed themselves. Their data always gets a confidence score. */
export const AUTOMATED_SOURCES: readonly PaymentSource[] = [
  'apple-pay-shortcut',
  'maybank-quick-capture',
  'tng-quick-capture',
  'maybank-receipt',
  'tng-receipt',
  'csv-import',
  'json-import',
];

export const isAutomatedSource = (source: PaymentSource): boolean =>
  AUTOMATED_SOURCES.includes(source);

// --- primitives ---------------------------------------------------------

export const uuidSchema = z.string().uuid();

/** Integer sen. Magnitude only: direction is carried by `type`. */
export const senSchema = z
  .number()
  .int('Amount must be a whole number of sen')
  .safe()
  .min(0, 'Amount cannot be negative')
  .max(100_000_000_00, 'Amount is unrealistically large');

export const currencySchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z]{3}$/, 'Currency must be a 3-letter code')
  .default('MYR');

export const isoDateTimeSchema = z
  .string()
  .trim()
  .refine((value) => !Number.isNaN(Date.parse(value)), 'Not a valid date and time')
  .transform((value) => new Date(value).toISOString());

/**
 * Free text arriving from an import, a Shortcut or a URL. Control characters
 * are stripped and the length is capped; the UI escapes on render as well.
 */
export const untrustedTextSchema = (max: number) =>
  z
    .string()
    .max(max * 4, 'Text is too long')
    // eslint-disable-next-line no-control-regex
    .transform((value) => value.replace(/[\u0000-\u001f\u007f-\u009f]/g, ' ').trim())
    .pipe(z.string().max(max, 'Text is too long'));

export const merchantSchema = untrustedTextSchema(120);
export const noteSchema = untrustedTextSchema(500);
export const accountLabelSchema = untrustedTextSchema(60);
export const categoryIdSchema = z
  .string()
  .trim()
  .min(1)
  .max(64)
  .regex(/^[a-z0-9][a-z0-9-]*$/, 'Category ids are lowercase, dash-separated');

export const confidenceSchema = z.number().min(0).max(1);

// --- transaction --------------------------------------------------------

export const transactionSchema = z.object({
  id: uuidSchema,
  type: z.enum(TRANSACTION_TYPES),
  amountSen: senSchema,
  currency: currencySchema,
  merchantOriginal: merchantSchema,
  merchantNormalized: merchantSchema,
  categoryId: categoryIdSchema,
  accountLabel: accountLabelSchema,
  source: z.enum(PAYMENT_SOURCES),
  occurredAt: isoDateTimeSchema,
  note: noteSchema.optional().nullable(),
  importConfidence: confidenceSchema,
  reviewStatus: z.enum(REVIEW_STATUSES),
  dedupeKey: z.string().min(1).max(256),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
  /** Soft delete, so a deletion can replicate instead of resurrecting on sync. */
  deletedAt: isoDateTimeSchema.nullable().optional(),
});
export type Transaction = z.infer<typeof transactionSchema>;

/** What the UI hands to `createTransaction` -- the derived fields are filled in for it. */
export const transactionDraftSchema = z.object({
  id: uuidSchema.optional(),
  type: z.enum(TRANSACTION_TYPES).default('expense'),
  amountSen: senSchema,
  currency: currencySchema.optional(),
  merchant: merchantSchema.optional(),
  categoryId: categoryIdSchema.optional(),
  accountLabel: accountLabelSchema.optional(),
  source: z.enum(PAYMENT_SOURCES).default('manual'),
  occurredAt: isoDateTimeSchema.optional(),
  note: noteSchema.optional().nullable(),
  importConfidence: confidenceSchema.optional(),
  reviewStatus: z.enum(REVIEW_STATUSES).optional(),
});
export type TransactionDraft = z.input<typeof transactionDraftSchema>;

// --- categories, accounts, rules ----------------------------------------

export const categorySchema = z.object({
  id: categoryIdSchema,
  name: untrustedTextSchema(40),
  /** Token name from the design system, not a raw colour, so themes stay coherent. */
  tone: z.enum([
    'amber',
    'green',
    'blue',
    'violet',
    'rose',
    'teal',
    'slate',
    'orange',
    'cyan',
    'lime',
  ]),
  icon: z.string().max(8).default('•'),
  isBuiltIn: z.boolean().default(false),
  /** Transfers are tracked but never counted as spending. */
  excludeFromSpending: z.boolean().default(false),
  archived: z.boolean().default(false),
  sortOrder: z.number().int().default(0),
  updatedAt: isoDateTimeSchema,
});
export type Category = z.infer<typeof categorySchema>;

export const accountSchema = z.object({
  id: uuidSchema,
  label: accountLabelSchema,
  /** A human hint only -- no card numbers are ever stored. */
  kind: z.enum(['card', 'ewallet', 'bank', 'cash', 'other']).default('other'),
  isDefault: z.boolean().default(false),
  archived: z.boolean().default(false),
  updatedAt: isoDateTimeSchema,
});
export type Account = z.infer<typeof accountSchema>;

export const MATCH_TYPES = ['contains', 'equals', 'starts-with'] as const;
export type MatchType = (typeof MATCH_TYPES)[number];

export const merchantRuleSchema = z.object({
  id: uuidSchema,
  pattern: untrustedTextSchema(80),
  matchType: z.enum(MATCH_TYPES).default('contains'),
  categoryId: categoryIdSchema,
  /** Optional merchant name to rewrite matches to, e.g. "TNG*KOPI KITA" -> "Kopi Kita". */
  renameTo: merchantSchema.optional().nullable(),
  priority: z.number().int().min(0).max(999).default(100),
  enabled: z.boolean().default(true),
  updatedAt: isoDateTimeSchema,
});
export type MerchantRule = z.infer<typeof merchantRuleSchema>;

// --- settings -----------------------------------------------------------

export const settingsSchema = z.object({
  monthlyBudgetSen: senSchema.default(0),
  currency: currencySchema,
  timeZone: z.string().min(1).default('Asia/Kuala_Lumpur'),
  defaultAccountLabel: accountLabelSchema.default('Cash'),
  defaultCategoryId: categoryIdSchema.default('other'),
  /** Cloud sync is opt-in. Everything works with this off. */
  syncEnabled: z.boolean().default(false),
  apiBaseUrl: z.string().max(300).default(''),
  /** Receipt images never leave the device unless this is explicitly turned on. */
  storeAttachmentsInCloud: z.boolean().default(false),
  theme: z.enum(['system', 'light', 'dark']).default('system'),
  updatedAt: isoDateTimeSchema,
});
export type Settings = z.infer<typeof settingsSchema>;

// --- shortcut ingestion -------------------------------------------------

/**
 * The payload an iPhone Shortcut POSTs. Deliberately forgiving about the
 * amount (Shortcuts hand over whatever the user typed) and deliberately strict
 * about everything else.
 */
export const shortcutIngestSchema = z.object({
  amount: z.union([z.string().min(1).max(32), z.number()]),
  /** Set when the Shortcut already sends whole sen, so no decimal parsing happens. */
  amountIsSen: z.boolean().optional(),
  currency: currencySchema.optional(),
  merchant: merchantSchema.optional().nullable(),
  source: z
    .enum(['apple-pay-shortcut', 'maybank-quick-capture', 'tng-quick-capture', 'manual'])
    .default('apple-pay-shortcut'),
  type: z.enum(TRANSACTION_TYPES).default('expense'),
  paidAt: isoDateTimeSchema.optional(),
  accountLabel: accountLabelSchema.optional().nullable(),
  note: noteSchema.optional().nullable(),
  idempotencyKey: z
    .string()
    .trim()
    .min(8, 'Idempotency key must be at least 8 characters')
    .max(128)
    .regex(/^[A-Za-z0-9_.:-]+$/, 'Idempotency key has unsupported characters'),
});
export type ShortcutIngestPayload = z.infer<typeof shortcutIngestSchema>;

/** Query parameters accepted by `/capture`. Treated exactly like network input. */
export const captureParamsSchema = z.object({
  source: z.enum(PAYMENT_SOURCES).default('manual'),
  /** Sen, as an integer string, so no decimal ambiguity in a URL. */
  amount: z
    .string()
    .regex(/^\d{1,10}$/, 'amount must be whole sen')
    .optional(),
  /**
   * The same amount in Ringgit, e.g. `12.90`.
   *
   * This exists so a Shortcut never has to multiply by 100. That multiplication
   * happens in binary floating point -- 12.90 * 100 is 1289.9999999999998 --
   * and a caller that truncates instead of rounding silently records RM 12.89.
   * Passing the decimal string straight through lets `parseAmountToSen` do the
   * conversion on the text, which is the only place in this project that
   * conversion is allowed to happen.
   *
   * If both are supplied, `amount` wins, because whole sen is unambiguous.
   */
  amountRm: z
    .string()
    .max(32)
    .refine((value) => {
      try {
        return Math.abs(parseAmountToSen(value)) > 0;
      } catch {
        return false;
      }
    }, 'amountRm must be an amount like 12.90')
    .optional(),
  merchant: merchantSchema.optional(),
  category: categoryIdSchema.optional(),
  account: accountLabelSchema.optional(),
  note: noteSchema.optional(),
  type: z.enum(TRANSACTION_TYPES).optional(),
  paidAt: isoDateTimeSchema.optional(),
});
export type CaptureParams = z.infer<typeof captureParamsSchema>;

export interface CaptureParseResult {
  /** Everything that validated. Fields that did not are simply absent. */
  params: Partial<CaptureParams> & { source: PaymentSource };
  /** Plain-language notes about what was ignored, shown above the form. */
  problems: string[];
}

const CAPTURE_FIELD_LABELS: Record<string, string> = {
  amount: 'amount',
  amountRm: 'amount',
  merchant: 'merchant',
  category: 'category',
  account: 'account',
  note: 'note',
  type: 'type',
  paidAt: 'date',
  source: 'source',
};

/**
 * Read a capture link field by field, keeping whatever is usable.
 *
 * A Shortcut is hand-assembled on a phone, and the most common mistake is a
 * magic variable that did not attach -- which arrives as an empty parameter.
 * Refusing the whole link for that strands the person on an error page holding
 * a receipt. Instead every field is validated on its own: the good ones prefill
 * the form, the bad ones are named, and the person finishes the entry by hand.
 *
 * Nothing is saved either way until they press the button, so being lenient
 * here costs no correctness -- `captureParamsSchema` still governs what a valid
 * link looks like, and the amount is never guessed.
 */
export function parseCaptureParams(raw: Record<string, string>): CaptureParseResult {
  const shape = captureParamsSchema.shape;
  const params: Record<string, unknown> = {};
  const problems: string[] = [];

  for (const [key, value] of Object.entries(raw)) {
    const field = (shape as Record<string, z.ZodTypeAny | undefined>)[key];
    if (!field) continue; // Unknown parameters are ignored in silence.

    // An empty parameter is a Shortcut that did not fill it in, not an attack.
    if (value.trim() === '') {
      problems.push(`The ${CAPTURE_FIELD_LABELS[key] ?? key} in the link was empty.`);
      continue;
    }

    const result = field.safeParse(value);
    if (result.success) {
      params[key] = result.data;
    } else {
      problems.push(
        `The ${CAPTURE_FIELD_LABELS[key] ?? key} in the link could not be read, so it was left out.`,
      );
    }
  }

  return {
    params: { ...params, source: (params.source as PaymentSource) ?? 'manual' },
    problems,
  };
}

// --- sync ---------------------------------------------------------------

export const syncPushSchema = z.object({
  transactions: z.array(transactionSchema).max(500),
  clientId: z.string().min(1).max(64),
});
export type SyncPushPayload = z.infer<typeof syncPushSchema>;

export const syncPullQuerySchema = z.object({
  since: isoDateTimeSchema.optional(),
  limit: z.coerce.number().int().min(1).max(1000).default(500),
});

// --- backup -------------------------------------------------------------

export const BACKUP_SCHEMA_VERSION = 1;

export const backupSchema = z.object({
  schemaVersion: z.literal(BACKUP_SCHEMA_VERSION),
  app: z.literal('what-i-spent'),
  exportedAt: isoDateTimeSchema,
  settings: settingsSchema.partial().optional(),
  categories: z.array(categorySchema).max(500).optional(),
  accounts: z.array(accountSchema).max(200).optional(),
  merchantRules: z.array(merchantRuleSchema).max(1000).optional(),
  transactions: z.array(transactionSchema).max(100_000),
});
export type Backup = z.infer<typeof backupSchema>;

// --- api envelopes ------------------------------------------------------

export const loginSchema = z.object({
  email: z.string().trim().toLowerCase().email().max(254),
  password: z.string().min(10, 'Use at least 10 characters').max(200),
});

export const apiErrorSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.unknown().optional(),
  }),
});
export type ApiError = z.infer<typeof apiErrorSchema>;
